import axios from 'axios';
import { getServerErrorKind, notifyServerError } from 'src/services/serverErrorNotifier';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  // Le refreshToken voyage désormais dans un cookie httpOnly posé par le serveur
  // (voir requestNewTokens ci-dessous) : withCredentials fait envoyer ce cookie
  // automatiquement par le navigateur, sans que le JS y ait jamais accès.
  withCredentials: true,
  // Sans délai, axios attend indéfiniment : quand le back-end ne répond plus,
  // nginx ne renvoie son 504 qu'au bout d'une minute ou plus. Après 30 s on
  // abandonne et on affiche le message « serveur injoignable ».
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Les envois de fichiers peuvent être lents sur une petite connexion : délai plus long.
var UPLOAD_TIMEOUT = 120000;

api.interceptors.request.use(
  function(config) {
    var token = localStorage.getItem('accessToken');

    if (token) {
      config.headers.Authorization = 'Bearer ' + token;
    } else {
      delete config.headers.Authorization;
    }

    var storedLang = localStorage.getItem('i18nextLng');
    var lang = storedLang ? storedLang.split('-')[0] : 'fr';
    config.headers['Accept-Language'] = lang;

    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.timeout < UPLOAD_TIMEOUT) {
      config.timeout = UPLOAD_TIMEOUT;
    }

    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

// Renouvellement automatique de session : le jeton d'accès expire très vite côté
// serveur (5 minutes). Sans ça, l'utilisateur était déconnecté brutalement dès
// qu'une requête échouait avec 401, même en pleine utilisation de l'application.
// Un seul appel à /refresh à la fois : si plusieurs requêtes échouent en même
// temps, elles partagent la même tentative de renouvellement au lieu d'en
// déclencher une chacune.
//
// Le refreshToken n'est plus lu depuis localStorage : le serveur le pose comme
// cookie httpOnly à la connexion (voir authSlice.signin) et withCredentials le
// renvoie automatiquement ici. Un script XSS ne peut donc plus le voler.
var refreshingTokenPromise = null;

function requestNewTokens() {
  return axios
    .post(
      process.env.REACT_APP_API_BASE_URL + '/auth/refresh',
      {},
      { withCredentials: true }
    )
    .then(function(response) {
      localStorage.setItem('accessToken', response.data.accessToken);
      return response.data.accessToken;
    });
}

api.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    var originalRequest = error.config;
    var status = error.response ? error.response.status : null;
    // Routes publiques (hors session) : un 401 y signifie "identifiants/email
    // invalides", pas "session expirée". Il ne faut donc ni tenter de refresh ni
    // rediriger vers la connexion — sinon l'utilisateur quitte la page en cours.
    var url = originalRequest ? String(originalRequest.url) : '';
    var isAuthEndpoint =
      url.indexOf('/auth/refresh') !== -1 ||
      url.indexOf('/auth/signin') !== -1 ||
      url.indexOf('/auth/forgot-password') !== -1 ||
      url.indexOf('/auth/reset-password-from-email') !== -1 ||
      url.indexOf('/auth/validate-reset-password-token') !== -1;

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      if (!refreshingTokenPromise) {
        refreshingTokenPromise = requestNewTokens().finally(function() {
          refreshingTokenPromise = null;
        });
      }

      // then(succès, échec) et non then().catch() : seul un échec du /refresh doit
      // déconnecter. Si c'est la requête rejouée qui échoue (ex. 500 à l'envoi d'un
      // mail), l'erreur remonte normalement à l'écran, la session reste ouverte.
      return refreshingTokenPromise.then(
        function(newAccessToken) {
          originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
          return api(originalRequest);
        },
        function(refreshError) {
          // Internet coupé ou serveur en panne pendant le /refresh : la session est
          // peut-être encore valide, on prévient au lieu de déconnecter.
          if (getServerErrorKind(refreshError)) {
            notifyServerError(refreshError);
            return Promise.reject(refreshError);
          }
          // Le jeton de renouvellement est lui aussi expiré ou invalide : on ne
          // peut plus rien faire silencieusement, l'utilisateur doit se reconnecter.
          localStorage.removeItem('accessToken');
          window.location.href = '/';
          return Promise.reject(refreshError);
        }
      );
    }

    // Internet coupé, serveur en maintenance ou en erreur (5xx) : message global.
    // La page de connexion affiche déjà l'erreur sous le formulaire, et un appel
    // peut s'en dispenser avec { skipGlobalErrorToast: true }.
    // Hors session (pages publiques), chaque page affiche ses propres erreurs.
    var isSignin = url.indexOf('/auth/signin') !== -1;
    var isLoggedIn = !!localStorage.getItem('accessToken');
    if (!isSignin && isLoggedIn && !(originalRequest && originalRequest.skipGlobalErrorToast)) {
      notifyServerError(error);
    }

    return Promise.reject(error);
  }
);

export default api;
